// ブランド品番/OPPI品番の採番制御と、保存時の採番確定をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createHinbanNumberingService(deps) {
    const {
        BRAND_HINBAN_COLUMNID,
        BRAND_HINBAN_SERIAL_DATAID,
        BRAND_HINBAN_SERIAL_LABEL,
        DicVal,
        FIXED_HINBAN_NUMBERING_COLUMNID,
        applyBrandHinbanCodeToEntryData,
        applyFirstExistingValue,
        assignEntryDataCurrent,
        dispatch,
        entrydata,
        entrydataRef,
        entryrerender,
        firstNonEmptyValue,
        fnc,
        getEntryDataValue,
        hasBrandHinbanPrerequisites,
        isEmpty,
        matchesColumnId,
        normalizeBrandHinbanSerialValue,
        protectHbSeasonCommit,
        rerender,
        sanitizeEntryData,
        setEntryDataValue,
        setSubBrandFieldOptions,
        textfieldsall
    } = deps;

    function findBrandHinbanSerialField() {
        if (isEmpty(textfieldsall)) return null;
        return textfieldsall.find((field) => {
            const dataid = String(DicVal(field, "dataid", "")).trim().toUpperCase();
            const label = String(DicVal(field, "columnnm", "")).trim();
            return matchesColumnId(DicVal(field, "columnid", ""), BRAND_HINBAN_COLUMNID) || dataid === BRAND_HINBAN_SERIAL_DATAID || label === BRAND_HINBAN_SERIAL_LABEL;
        }) || null;
    }
    function isAutomaticHinbanNumberingSelected(targetData) {
        return String(DicVal(targetData, FIXED_HINBAN_NUMBERING_COLUMNID, "0")).trim() === "0";
    }
    function isManualHinbanNumberingSelected(targetData) {
        return String(DicVal(targetData, FIXED_HINBAN_NUMBERING_COLUMNID, "0")).trim() === "1";
    }
    function isBrandHinbanNumberingControlledField(field) {
        if (isEmpty(field)) return false;
        const dataid = String(DicVal(field, "dataid", "")).trim().toUpperCase();
        const label = String(DicVal(field, "columnnm", "")).trim();
        return matchesColumnId(DicVal(field, "columnid", ""), BRAND_HINBAN_COLUMNID) || dataid === BRAND_HINBAN_SERIAL_DATAID || label === BRAND_HINBAN_SERIAL_LABEL;
    }
    function applyBrandHinbanNumberingLockToField(field) {
        if (!isBrandHinbanNumberingControlledField(field)) return field;
        if (isManualHinbanNumberingSelected(entrydataRef.current)) {
            return { ...field, locked: false, unchange: false, primary: false, editableOnNewFormOnly: false };
        }
        if (isAutomaticHinbanNumberingSelected(entrydataRef.current)) {
            return { ...field, locked: true, unchange: true };
        }
        return field;
    }
    function applyBrandHinbanNumberingLockToFields(fields) {
        if (isEmpty(fields)) return fields;
        return fields.map(applyBrandHinbanNumberingLockToField);
    }
    function ensureBrandHinbanValuesOnSave(onSuccess, onError) {
        const notifyError = typeof onError === "function" ? onError : null;
        const targetData = DicVal(entrydataRef, "current", null);
        if (!hasBrandHinbanPrerequisites(targetData)) {
            if (typeof onSuccess === "function") onSuccess();
            return;
        }

        const currentBrandHinbanCode = String(getEntryDataValue(targetData, BRAND_HINBAN_COLUMNID, "")).trim();
        if (!isEmpty(currentBrandHinbanCode)) {
            if (typeof onSuccess === "function") onSuccess();
            return;
        }

        const serialField = findBrandHinbanSerialField();
        const serialColumnId = String(DicVal(serialField, "columnid", "")).trim();
        const serialValue = isEmpty(serialColumnId) ? "" : normalizeBrandHinbanSerialValue(getEntryDataValue(targetData, serialColumnId, ""));
        if (!isAutomaticHinbanNumberingSelected(targetData) && !isEmpty(serialValue)) {
            applyBrandHinbanCodeToEntryData(targetData, serialValue);
            if (typeof onSuccess === "function") onSuccess();
            return;
        }

        if (!isAutomaticHinbanNumberingSelected(targetData)) {
            if (typeof onSuccess === "function") onSuccess();
            return;
        }


        fnc.current.postaxios("hinbanmaster/GetDefaultBrandHinban",
            { data: sanitizeEntryData(targetData) },
            {
                onSuccess: function (data) {


                    if (Array.isArray(data.warning) && data.warning.length !== 0) {
                        if (notifyError) notifyError(data);
                        return;
                    }
                    const brandHinbanSerial = String(firstNonEmptyValue(DicVal(data, "returnData", {}), ["BrandHinban"]) || "").trim();
                    if (isEmpty(brandHinbanSerial)) {
                        if (notifyError) notifyError(data);
                        return;
                    }
                    applyBrandHinbanCodeToEntryData(targetData, brandHinbanSerial);
                    if (typeof onSuccess === "function") onSuccess();
                },
                onError: function (data) {
                    if (notifyError) notifyError(data);
                }
            }, true
        );
    }
    function ensureSubBrandValuesOnSave(onSuccess) {
        const targetData = DicVal(entrydataRef, "current", null);
        const brandCode = String(getEntryDataValue(targetData, "PDT_BRDCODE", "")).trim();
        const subBrandCode = String(getEntryDataValue(targetData, "PDT_SBRCODE", "")).trim();
        if (isEmpty(brandCode) || !isEmpty(subBrandCode)) {
            if (typeof onSuccess === "function") onSuccess();
            return;
        }

        fnc.current.postaxios("hinbanmaster/GetDefaultSubBrandCode",
            { PDT_BRDCODE: brandCode },
            {
                onSuccess: function (data) {
                    const returnData = DicVal(data, "returnData", {});
                    const nextSubBrandCode = String(firstNonEmptyValue(returnData, ["PDT_SBRCODE", "SBR_CODE"]) || "").trim();
                    const nextSubBrandName = String(DicVal(returnData, "SBR_SUBNAME", "")).trim();
                    let changed = false;
                    if (!isEmpty(nextSubBrandCode)) {
                        changed = setEntryDataValue(targetData, "PDT_SBRCODE", nextSubBrandCode) || changed;
                        if (!isEmpty(nextSubBrandName)) {
                            changed = setEntryDataValue(targetData, "PDT_SBRCODE_nm", nextSubBrandName) || changed;
                            setSubBrandFieldOptions([{ code: nextSubBrandCode, name: nextSubBrandName }]);
                        }
                    }
                    if (changed) {
                        assignEntryDataCurrent(protectHbSeasonCommit(targetData, "targetData"), "targetData");
                        dispatch(entrydata({ ...entrydataRef.current }));
                        dispatch(entryrerender());
                        rerender();
                    }
                    if (typeof onSuccess === "function") onSuccess();
                },
                onError: function () {
                    if (typeof onSuccess === "function") onSuccess();
                }
            }, true
        );
    }
    function normalizePsHinbanYearSuffix(yearValue) {
        const digits = String(isEmpty(yearValue) ? "" : yearValue).replace(/\D/g, "").trim();
        if (isEmpty(digits)) return "";
        return digits.length >= 2 ? digits.slice(-2) : digits.padStart(2, "0");
    }
    function normalizePsHinbanSeasonCode(seasonValue) {
        const text = String(isEmpty(seasonValue) ? "" : seasonValue).trim();
        if (isEmpty(text)) return "";
        return text.slice(0, 1);
    }
    function normalizePsHinbanBrandCode(brandValue) {
        const text = String(isEmpty(brandValue) ? "" : brandValue).trim();
        if (isEmpty(text)) return "";
        return text;
    }
    function buildPsHinbanPrefix(data) {
        const yearSuffix = normalizePsHinbanYearSuffix(firstNonEmptyValue(data, ["PDT_YEAR", "PDT_Year"]));
        if (isEmpty(yearSuffix)) return { ok: false, message: "PDT_Year が未設定のため PS品番を確定できません。" };

        const seasonCode = normalizePsHinbanSeasonCode(firstNonEmptyValue(data, ["PDT_SEASONCD", "PDT_SeasonCd"]));
        if (isEmpty(seasonCode)) return { ok: false, message: "シーズンコード(PDT_SeasonCd) が未設定のため PS品番を確定できません。" };
        if (seasonCode.length !== 1) return { ok: false, message: "シーズンコード(PDT_SeasonCd) は1桁である必要があります。" };

        const brandCode = normalizePsHinbanBrandCode(firstNonEmptyValue(data, ["PDT_BRDCODE", "PDT_BRDCODE_cd", "PDT_BRDCode", "PDT_BRANDCD", "PDT_BRAND"]));
        if (isEmpty(brandCode)) return { ok: false, message: "ブランドコード(PDT_BRDCode) が未設定のため PS品番を確定できません。" };
        if (brandCode.length !== 3) return { ok: false, message: "ブランドコード(PDT_BRDCode) は3桁である必要があります。" };

        return {
            ok: true,
            prefix: `P${yearSuffix}${seasonCode}${brandCode}`,
            year: firstNonEmptyValue(data, ["PDT_YEAR", "PDT_Year"]),
            seasonCode: seasonCode,
            brandCode: brandCode
        };
    }
    function normalizePsHinbanSequence(sequenceValue) {
        const digits = String(isEmpty(sequenceValue) ? "" : sequenceValue).replace(/\D/g, "").trim();
        if (isEmpty(digits)) return "";
        return digits.padStart(5, "0").slice(-5);
    }
    function requestOppiHinbanSequence(prefix, numberingSource, callbacks) {
        if (isEmpty(callbacks)) {
            callbacks = numberingSource;
            numberingSource = {};
        }
        const onSuccess = typeof callbacks?.onSuccess === "function" ? callbacks.onSuccess : null;
        const onError = typeof callbacks?.onError === "function" ? callbacks.onError : null;
        const formApi = DicVal(fnc, "current", null);
        if (isEmpty(formApi) || typeof formApi.postaxios !== "function") {
            if (!isEmpty(formApi) && typeof formApi.warnmsg === "function") {
                formApi.warnmsg("品番採番APIを呼び出す前に画面初期化が完了していません。");
            }
            return;
        }
        formApi.postaxios("hinbanmaster/GetOppiHinbanSequence",
            {
                prefix: prefix,
                numberingId: "OppiHinban",
                year: String(DicVal(numberingSource, "year", "")).trim(),
                seasonCode: String(DicVal(numberingSource, "seasonCode", "")).trim(),
                brandCode: String(DicVal(numberingSource, "brandCode", "")).trim()
            },
            {
                onSuccess: function (data) {
                    if (Array.isArray(data.warning) && data.warning.length !== 0) {
                        const msg = data.warning.length + "件の制約違反により品番を確定できませんでした。";
                        if (typeof formApi.showwarndescription === "function") {
                            formApi.showwarndescription(msg, data.warning);
                        }
                        if (onError) onError(data);
                        return;
                    }
                    if (onSuccess) onSuccess(data);
                },
                onError: function (response) {
                    const message = String(DicVal(response, "errDescription", "")).trim();
                    if (typeof formApi.warnmsg === "function") {
                        formApi.warnmsg(message === "" ? "品番採番APIの呼び出しに失敗しました" : message);
                    }
                    if (onError) onError(response);
                }
            }, true
        );
    }
    function hasResolvedOppiCode(targetData) {
        return !isEmpty(firstNonEmptyValue(targetData, ["PDT_OPPICD", "PDT_OppiCd", "OPPICD"]));
    }
    function applyConfirmedOppiHinbanToEntryData(targetData, confirmedHinban) {
        if (isEmpty(targetData)) return false;
        const nextData = { ...targetData };
        let changed = false;
        changed = setEntryDataValue(nextData, "PDT_OPPICD", confirmedHinban) || changed;
        changed = applyFirstExistingValue(nextData, ["PDT_OppiCd", "OPPICD", "SJCM_OppiHinban", "SSD_OPPICD"], confirmedHinban) || changed;
        if (!changed) nextData.PDT_OPPICD = confirmedHinban;
        assignEntryDataCurrent(nextData, "applyConfirmedOppiHinbanToEntryData");
        dispatch(entrydata({ ...nextData }));
        dispatch(entryrerender());
        rerender();
        return true;
    }
    function ensureOppiHinbanAssigned(callbacks) {
        const onSuccess = typeof callbacks?.onSuccess === "function" ? callbacks.onSuccess : null;
        const onError = typeof callbacks?.onError === "function" ? callbacks.onError : null;
        const showSuccessMessage = callbacks?.showSuccessMessage === true;
        const currentEntryData = DicVal(entrydataRef, "current", null);
        const formApi = DicVal(fnc, "current", null);
        if (isEmpty(currentEntryData)) {
            if (!isEmpty(formApi) && typeof formApi.warnmsg === "function") {
                formApi.warnmsg("品番確定に必要な画面データがまだ読み込まれていません。");
            }
            if (onError) onError();
            return;
        }
        if (hasResolvedOppiCode(currentEntryData)) {
            if (onSuccess) onSuccess(firstNonEmptyValue(currentEntryData, ["PDT_OPPICD", "PDT_OppiCd", "OPPICD"]));
            return;
        }
        const buildResult = buildPsHinbanPrefix(currentEntryData);
        if (!buildResult.ok) {
            if (!isEmpty(formApi) && typeof formApi.warnmsg === "function") {
                formApi.warnmsg(buildResult.message);
            }
            if (onError) onError(buildResult);
            return;
        }
        const prefix = buildResult.prefix;
        requestOppiHinbanSequence(prefix, buildResult, {
            onSuccess: function (data) {
                const latestEntryData = DicVal(entrydataRef, "current", null);
                const latestFormApi = DicVal(fnc, "current", null);
                if (isEmpty(latestEntryData)) {
                    if (!isEmpty(latestFormApi) && typeof latestFormApi.warnmsg === "function") {
                        latestFormApi.warnmsg("品番確定結果の反映先データが見つかりません。");
                    }
                    if (onError) onError(data);
                    return;
                }
                const rawSequence = firstNonEmptyValue(DicVal(data, "returnData", {}), ["sequence", "seq", "number", "no", "nextNo", "hinbanNo"]);
                const sequence = normalizePsHinbanSequence(rawSequence);
                if (isEmpty(sequence)) {
                    if (!isEmpty(latestFormApi) && typeof latestFormApi.warnmsg === "function") {
                        latestFormApi.warnmsg("品番採番APIの応答に連番が含まれていません。");
                    }
                    if (onError) onError(data);
                    return;
                }
                const confirmedHinban = prefix + sequence;
                applyConfirmedOppiHinbanToEntryData(latestEntryData, confirmedHinban);
                if (showSuccessMessage && !isEmpty(latestFormApi) && typeof latestFormApi.successmsg === "function") {
                    latestFormApi.successmsg("PS品番を確定しました: " + confirmedHinban);
                }
                if (onSuccess) onSuccess(confirmedHinban);
            },
            onError: function (data) {
                if (onError) onError(data);
            }
        });
    }

    return {
        findBrandHinbanSerialField,
        isAutomaticHinbanNumberingSelected,
        isManualHinbanNumberingSelected,
        isBrandHinbanNumberingControlledField,
        applyBrandHinbanNumberingLockToField,
        applyBrandHinbanNumberingLockToFields,
        ensureBrandHinbanValuesOnSave,
        ensureSubBrandValuesOnSave,
        normalizePsHinbanYearSuffix,
        normalizePsHinbanSeasonCode,
        normalizePsHinbanBrandCode,
        buildPsHinbanPrefix,
        normalizePsHinbanSequence,
        requestOppiHinbanSequence,
        hasResolvedOppiCode,
        applyConfirmedOppiHinbanToEntryData,
        ensureOppiHinbanAssigned
    };
}
